import React from "react";
import { Box, Typography } from "@material-ui/core";
import CompactMessageCard from "./CompactMessageCard";
import MessagesTimelineTable from "./MessagesTimelineTable";
import { useStyles } from './MessagesTimelineStyles';

const MessagesTimeline = ({ batches, viewType = "table" }) => {
  const classes = useStyles();

  // Handle both old format (flat messages array) and new format (batches)
  const batchesData = batches || (Array.isArray(batches) ? [] : []);

  if (!batchesData || (Array.isArray(batchesData) && batchesData.length === 0)) {
    return (
      <Box className={classes.container}>
        <Typography className={classes.emptyState}>
          No messages available
        </Typography>
      </Box>
    );
  }

  // Flatten messages from all batches for table view
  const allMessages = batchesData.flatMap(batch => 
    batch.messages.map(msg => ({ ...msg, batchNumber: batch.batchNumber }))
  );

  // If table view, use the original timeline table component
  if (viewType === "table") {
    return <MessagesTimelineTable batches={batchesData} />;
  }

  // Get all unique column names across all messages for consistent alignment
  // Exclude only: id, timestamp, batchNumber, messageType (these are in header)
  // Include procedureNumber and all other fields in the content
  const getAllColumnNames = () => {
    const excludeFields = ["id", "timestamp", "batchNumber", "messageType"];
    const allColumnNames = new Set();
    
    // Map of field names to display column names
    const fieldToColumnMap = {
      "procedureNumber": "Procedure Number",
      "outletNumber": "Outlet Number",
      "cmoMaterialDescription": "CMO Material Description",
      "cmoMaterialCode": "CMO Material Code",
      "sponsorMaterialCode": "Sponsor Material Code",
      "cmoMaterialBatchNumber": "CMO Material Batch Number",
      "targetQuantity": "Target Quantity",
      "actualQuantity": "Actual Quantity",
      "parameter": "Parameter",
      "setPoint": "Set Point",
      "range": "Range",
      "actualValue": "Actual Value",
      "uom": "UOM",
      "methodNumber": "Method Number",
      "methodName": "Method Name",
      "dateOfSampling": "Date Of Sampling",
      "sampleID": "Sample ID",
      "sampleDescription": "Sample Description",
      "specificationLimit": "Specification Limit",
      "resultValue": "Result Value",
      "resultPassFail": "Result (Pass/Fail)",
      "deviationTitle": "Deviation Title",
      "dateOfDeviation": "Date Of Deviation",
      "assignedToRole": "Assigned To (Role)",
      "assignedToUser": "Assigned To (User)",
      "requireSponsorReviewSME": "Require Sponsor Review (SME)",
      "requireSponsorReviewQA": "Require Sponsor Review (QA)",
      "investigationNumber": "Investigation Number",
      "deviationStatus": "Deviation Status",
    };
    
    allMessages.forEach(message => {
      Object.keys(message).forEach(key => {
        if (!excludeFields.includes(key) && message[key] !== undefined && message[key] !== null && message[key] !== "") {
          const columnName = fieldToColumnMap[key] || key;
          allColumnNames.add(columnName);
        }
      });
    });
    
    return Array.from(allColumnNames);
  };

  const allColumnNames = getAllColumnNames();

  // Card view - show messages directly (all from same batch)
  return (
    <Box className={classes.container}>
      <Box className={classes.messagesList}>
        {batchesData.flatMap((batch) =>
          batch.messages.map((message, msgIdx) => (
            <CompactMessageCard
              key={message.id || msgIdx}
              message={{ ...message, batchNumber: batch.batchNumber }}
              index={msgIdx}
              allColumnNames={allColumnNames}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default MessagesTimeline;

